// src/services/cotizacionService.ts
import axiosInstance from '../api/axiosInstance'
import type { CotizacionData, LineItem } from '../types/cotizacionFormTypes'
import { getMeta } from '../utils/cotizacionFormUtils'
import { parseClienteNetoFlag } from '../utils/clienteNetoFlag'

/**
 * Crea un cliente en la API si no existe
 * @param formData - Datos del formulario de cotización
 * @throws Error si falta información requerida o si falla la creación (excepto 409)
 */
export async function createCustomerIfNeeded(formData: CotizacionData): Promise<void> {
  const metaTipoDocumento = getMeta(formData.meta_data || [], 'tipo_documento')
  const metaDocumento = getMeta(formData.meta_data || [], 'documento')
  
  if (!metaTipoDocumento || !metaDocumento) {
    throw new Error('Tipo de documento y documento son requeridos para crear el cliente')
  }

  const token = localStorage.getItem('accessToken')
  if (!token) {
    throw new Error('Token de autenticación no encontrado')
  }

  const clientePayload = {
    tipo_documento: metaTipoDocumento,
    document: metaDocumento,
    first_name: formData.billingAddress?.first_name || '',
    last_name: formData.billingAddress?.last_name || '',
    email: formData.customer?.email || '',
    phone: formData.billingAddress?.phone || '',
    tipo_persona: 'N',
    dir_1: formData.billingAddress?.address_1 || '',
    dir_2: formData.shippingAddress?.address_1 || '',
    idciudad_1: formData.billingAddress?.city || '',
    idciudad_2: formData.shippingAddress?.city || '',
  }

  try {
    await axiosInstance.post('/customers', clientePayload, {
      headers: { Authorization: `Bearer ${token}` }
    })
  } catch (error: any) {
    // Si el cliente ya existe, no es un error
    if (error.response?.status !== 409) {
      throw new Error('Error al crear el cliente. Verifica los datos e inténtalo de nuevo.')
    }
  }
}

/**
 * Asegura que todos los productos en line_items tengan nombres
 * Modifica el array line_items directamente
 * @param lineItems - Array de items de la cotización (se modifica in-place)
 */
export async function ensureProductNames(lineItems: LineItem[]): Promise<void> {
  if (!lineItems?.length) return

  const token = localStorage.getItem('accessToken')
  if (!token) return

  for (let i = 0; i < lineItems.length; i++) {
    const item = lineItems[i]
    if (!item.name || !item.product_name) {
      try {
        const resp = await axiosInstance.get(`/products/${item.product_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (resp.data?.name) {
          lineItems[i].name = resp.data.name
          lineItems[i].product_name = resp.data.name
        }
      } catch (e) {
        // Si falla, deja los campos como están
        console.warn(`No se pudo obtener el nombre del producto ${item.product_id}`)
      }
    }
  }
}

/**
 * Busca un cliente por tipo de documento y documento
 * @param tipoDocumento - Tipo de documento del cliente
 * @param documento - Número de documento del cliente
 * @returns Datos del cliente encontrado o null si no existe
 */
export async function findCustomer(
  tipoDocumento: string,
  documento: string
): Promise<any | null> {
  const token = localStorage.getItem('accessToken')
  if (!token) {
    throw new Error('Token de autenticación no encontrado')
  }

  try {
    const response = await axiosInstance.get(
      `/customers/find?tipo_documento=${tipoDocumento}&document=${documento}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    )
    return response.data || null
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null
    }
    throw error
  }
}

/**
 * Busca productos por término de búsqueda
 * @param searchTerm - Término de búsqueda (mínimo 4 caracteres)
 * @returns Array de productos encontrados
 */
export async function searchProducts(searchTerm: string): Promise<any[]> {
  if (searchTerm.length < 4) {
    return []
  }

  const token = localStorage.getItem('accessToken')
  if (!token) {
    return []
  }

  try {
    const response = await axiosInstance.get(
      `/products?search=${encodeURIComponent(searchTerm)}&limit=10&page=1&skipOfima=false&includeInactive=true`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    )
    const products = response.data?.data || []
    return Array.isArray(products) ? products : []
  } catch (err) {
    return []
  }
}

/**
 * Prepara los meta_data para el envío a la API
 * @param formData - Datos del formulario
 * @param costoEnvioInputValue - Valor del costo de envío
 * @param mode - Modo del formulario
 * @returns Array de meta_data preparado
 */
export function prepareMetaData(
  formData: CotizacionData,
  costoEnvioInputValue: string,
  mode: 'view' | 'edit' | 'create'
): Array<{ key: string; value: string }> {
  let meta_data = Array.isArray(formData.meta_data) ? [...formData.meta_data] : []
  
  // Asegurar que costo_envio exista y esté actualizado con el valor del input
  const costoEnvioIndex = meta_data.findIndex((m: any) => m.key === 'costo_envio')
  if (costoEnvioIndex >= 0) {
    meta_data[costoEnvioIndex].value = costoEnvioInputValue
  } else {
    meta_data.push({ key: 'costo_envio', value: costoEnvioInputValue })
  }

  // Asegurar que tipo_documento y documento estén en meta_data
  const tipoDocumento = getMeta(formData.meta_data || [], 'tipo_documento') || formData.customer?.tipo_documento || ''
  const documento = getMeta(formData.meta_data || [], 'documento') || formData.customer?.documento || ''
  
  // Actualizar o agregar tipo_documento
  const tipoDocIndex = meta_data.findIndex((m: any) => m.key === 'tipo_documento')
  if (tipoDocIndex >= 0) {
    meta_data[tipoDocIndex].value = tipoDocumento.toString()
  } else if (tipoDocumento) {
    meta_data.push({ key: 'tipo_documento', value: tipoDocumento.toString() })
  }

  // Actualizar o agregar documento
  const docIndex = meta_data.findIndex((m: any) => m.key === 'documento')
  if (docIndex >= 0) {
    meta_data[docIndex].value = documento.toString()
  } else if (documento) {
    meta_data.push({ key: 'documento', value: documento.toString() })
  }

  // Asegurar que expiration_limit esté presente en modo create
  if (mode === 'create') {
    const expirationLimit = getMeta(formData.meta_data || [], 'expiration_limit') || formData.extraDetails?.expiration_limit || ''
    const expIndex = meta_data.findIndex((m: any) => m.key === 'expiration_limit')
    if (expIndex >= 0) {
      meta_data[expIndex].value = expirationLimit.toString()
    } else if (expirationLimit) {
      meta_data.push({ key: 'expiration_limit', value: expirationLimit.toString() })
    }
  }

  const clienteNeto = parseClienteNetoFlag(formData.extraDetails?.cliente_neto)
  const cnIdx = meta_data.findIndex((m: any) => m.key === 'cliente_neto')
  if (cnIdx >= 0) {
    meta_data[cnIdx].value = clienteNeto ? '1' : '0'
  } else {
    meta_data.push({ key: 'cliente_neto', value: clienteNeto ? '1' : '0' })
  }

  return meta_data
}

/**
 * Prepara los datos del formulario para el envío a la API
 * @param formData - Datos del formulario
 * @param costoEnvioInputValue - Valor del costo de envío
 * @param mode - Modo del formulario
 * @param calculateTotalsFn - Función para calcular totales
 * @returns Datos preparados para enviar a la API
 */
export function prepareFormData(
  formData: CotizacionData,
  costoEnvioInputValue: string,
  mode: 'view' | 'edit' | 'create',
  calculateTotalsFn: (items: any[], shipping: string) => any
): any {
  const { billingAddress, shippingAddress, customer, ...rest } = formData
  
  // Preparar meta_data
  const meta_data = prepareMetaData(formData, costoEnvioInputValue, mode)
  
  // Calcular el total correcto que incluya el costo de envío
  const totals = calculateTotalsFn(formData.line_items || [], costoEnvioInputValue)
  
  // Preparar datos según el modo
  const dataToSend = {
    ...rest,
    billing: { ...billingAddress },
    shipping: { ...shippingAddress },
    meta_data,
    total: totals.total, // Enviar el total calculado que incluye envío
    extraDetails: {
      ...rest.extraDetails,
      costo_envio: costoEnvioInputValue
    }
  }

  // Agregar agente solo en modo create
  if (mode === 'create') {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    dataToSend.agente = user.id || ''
  }

  return dataToSend
}

